// main imports
import React, { Component } from "react";
import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import { downloadAndOpenFile } from "@/lib/Backend.rest";
import * as t from "@/lib/backend/common.generated.types";
import { waitForJob, fullSimulation, fullInsights, calculateKpis, recomputeKnowledgeGraph } from "@/lib/BusinessLogic";
import ErrorComponent from "@/components/ErrorComponent";

// This should be the type of t.IGetJobInfoRes["result"]
type JobResult = { results: { passed: boolean; title: string; description: string }[]; all_checks: number };

// defining types
type Props = {};
type State = {
    anonymizeEnabled: boolean;
    anonymizeTitle: string;
    logFiles: t.ILogFileItem[];
    message: string;
    optimizeJobStorageEnabled: boolean;
    optimizeJobStorageTitle: string;
    purgeUnconfirmedStockRequirementsEnabled: boolean;
    purgeUnconfirmedStockRequirementsTitle: string;
    refreshDashboardsEnabled: boolean;
    refreshDashboardsTitle: string;
    refreshDeleteDanglingReportsEnabled: boolean;
    refreshDeleteDanglingReportsTitle: string;
    refreshDeleteOldReportsEnabled: boolean;
    refreshDeleteOldReportsTitle: string;
    runRecalculateInsightsEnabled: boolean;
    runRecalculateInsightsTitle: string;
    runRecomputeKPIsEnabled: boolean;
    runRecomputeKPIsTitle: string;
    runJobStatsKpiEnabled: boolean;
    runJobStatsKpiTitle: string;
    runRecomputeKnowledgeGraphEnabled: boolean;
    runRecomputeKnowledgeGraphTitle: string;
    runAutocalculateLineCompetencesEnabled: boolean;
    runAutocalculateLineCompetencesTitle: string;
    runUnconfirmedStockEnabled: boolean;
    runUnconfirmedStockTitle: string;
    runUpdateProducedAvailableEnabled: boolean;
    runUpdateProducedAvailableTitle: string;
    runSimulationEnabled: boolean;
    runSimulationTitle: string;
    runToolSetupMatrixEnabled: boolean;
    runToolSetupMatrixTitle: string;
    toolSetupCsvParsingFormat: "all_to_all" | "all_to_last";
    runChecksCtrl: React.ReactNode;
    runChecksEnabled: boolean;
    runPwdPolicyEnabled: boolean;
    runPwdPolicyTitle: string;
    showLogFilesEnabled: boolean;
    showLogFilesTitle: string;
};

/**
 * Displaying actions that administrator can perform.
 */
class ManufacturingAdmin extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const state: State = {
            anonymizeEnabled: true,
            anonymizeTitle: "",
            optimizeJobStorageEnabled: true,
            optimizeJobStorageTitle: "",
            refreshDashboardsEnabled: true,
            refreshDashboardsTitle: "",
            refreshDeleteDanglingReportsEnabled: true,
            refreshDeleteDanglingReportsTitle: "",
            refreshDeleteOldReportsEnabled: true,
            refreshDeleteOldReportsTitle: "",
            purgeUnconfirmedStockRequirementsEnabled: true,
            purgeUnconfirmedStockRequirementsTitle: "",
            showLogFilesTitle: "",
            message: "",
            logFiles: [],
            showLogFilesEnabled: true,
            runChecksEnabled: true,
            runChecksCtrl: <span></span>,
            runPwdPolicyEnabled: true,
            runPwdPolicyTitle: "",
            runRecalculateInsightsEnabled: true,
            runRecalculateInsightsTitle: "",
            runRecomputeKPIsEnabled: true,
            runRecomputeKPIsTitle: "",
            runJobStatsKpiEnabled: true,
            runJobStatsKpiTitle: "",
            runRecomputeKnowledgeGraphEnabled: true,
            runRecomputeKnowledgeGraphTitle: "",
            runAutocalculateLineCompetencesEnabled: true,
            runAutocalculateLineCompetencesTitle: "",
            runUnconfirmedStockEnabled: true,
            runUnconfirmedStockTitle: "",
            runUpdateProducedAvailableEnabled: true,
            runUpdateProducedAvailableTitle: "",
            runSimulationEnabled: true,
            runSimulationTitle: "",
            runToolSetupMatrixEnabled: true,
            runToolSetupMatrixTitle: "",
            toolSetupCsvParsingFormat: "all_to_last",
        };
        this.state = state;
    }

    /**
     * Handler for clicking the "Apply password policy" button.
     */
    async handleApplyPwdPolicy() {
        this.setState({
            runPwdPolicyEnabled: false,
            runPwdPolicyTitle: "Applying passwor dpolicy...",
        });
        try {
            await getBackend2().users.applyPasswordPolicy({});
            this.setState({
                runPwdPolicyEnabled: true,
                runPwdPolicyTitle: "done",
            });
        } catch (err: unknown) {
            this.setState({
                runPwdPolicyEnabled: true,
                runPwdPolicyTitle: "Error: " + (err as Error).message,
            });
        }
    }

    /**
     * Handler for clicking the "Show log files" button.
     */
    async handleLoadLogFiles() {
        this.setState({
            logFiles: [],
            showLogFilesEnabled: false,
            showLogFilesTitle: "Loading log files...",
        });
        try {
            const res = await getBackend2().common.getLogFiles({});
            this.setState({
                showLogFilesEnabled: false,
                logFiles: res.files,
                showLogFilesTitle: "",
            });
        } catch (err: unknown) {
            this.setState({
                logFiles: [],
                showLogFilesEnabled: true,
                showLogFilesTitle: "Error: " + (err as Error).message,
            });
        }
    }

    /**
     * Handler for clicking the "Optimize" button.
     */
    async handleOptimizeJobStorage() {
        this.setState({
            optimizeJobStorageEnabled: false,
            optimizeJobStorageTitle: "Waiting for server to finish...",
        });
        try {
            await getBackend2().common.optimizeJobFileStorage({});
            this.setState({
                optimizeJobStorageEnabled: true,
                optimizeJobStorageTitle: "Done.",
            });
        } catch (err: unknown) {
            this.setState({
                optimizeJobStorageEnabled: true,
                optimizeJobStorageTitle: "Error: " + (err as Error).message,
            });
        }
    }

    /**
     * Handler for clicking the "Refresh dashboards" button.
     */
    async handleRefreshDashboards() {
        this.setState({
            refreshDashboardsEnabled: false,
            refreshDashboardsTitle: "Waiting for server to finish...",
        });
        try {
            const res = await getBackend2().manufacturing.populateDashboards({});
            this.setState({
                refreshDashboardsEnabled: true,
                refreshDashboardsTitle: "Done, added " + res.count + " new dashboards.",
            });
        } catch (err: unknown) {
            this.setState({
                refreshDashboardsEnabled: true,
                refreshDashboardsTitle: "Error: " + (err as Error).message,
            });
        }
    }

    /**
     * Handler for clicking the "Run checks" button.
     */
    async handleRunChecks() {
        this.setState({
            runChecksEnabled: false,
            runChecksCtrl: <span>Waiting for server to finish...</span>,
        });
        try {
            const { token } = await getBackend2().manufacturing.performChecks({ only_failed_checks: false });
            const data: JobResult = await waitForJob(getBackend2(), token, true, true);
            const all_passed = data.results.filter(test => test.passed).length;
            const res = [
                <div key={"check_title"} className="space10">
                    Done, ran {data.all_checks} check(s), {all_passed} passed.
                </div>,
            ];
            data.results.forEach((test, i) => {
                const cssClass = "space10 check-result " + (test.passed ? "check-result-ok" : "check-result-error");
                res.push(
                    <div key={"check_index_" + i} className={cssClass}>
                        <div className="bold">
                            <span> {test.title} </span>
                        </div>
                        <div className="light">
                            <span> {test.description} </span>
                        </div>
                    </div>
                );
            });
            this.setState({
                runChecksEnabled: true,
                runChecksCtrl: res,
            });
        } catch (err: unknown) {
            this.setState({
                runChecksEnabled: true,
                runChecksCtrl: <span>Error: {String((err as Error).message)}</span>,
            });
        }
    }

    async handleDeleteDanglingReports(perform_delete: boolean) {
        this.setState({
            refreshDeleteDanglingReportsEnabled: false,
            refreshDeleteDanglingReportsTitle: "Waiting for server to finish...",
        });
        try {
            const res = await getBackend2().reports.cleanDanglingReportFiles({ perform_delete });

            this.setState({
                refreshDeleteDanglingReportsEnabled: true,
                refreshDeleteDanglingReportsTitle: perform_delete
                    ? `Done, deleted ${res.deleted_files_count}, remaining ${res.remaining_files_count}.`
                    : `Finished with check, ${res.deleted_files_count} for deletion, ${res.remaining_files_count} to remain.`,
            });
        } catch (err: unknown) {
            this.setState({
                refreshDeleteDanglingReportsEnabled: true,
                refreshDeleteDanglingReportsTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handlePurgeStockRequirements() {
        this.setState({
            purgeUnconfirmedStockRequirementsEnabled: false,
            purgeUnconfirmedStockRequirementsTitle: "Waiting for server to finish...",
        });
        try {
            await getBackend2().manufacturing.runPurgeUnconfirmedStockRequirements({});
            this.setState({
                purgeUnconfirmedStockRequirementsEnabled: true,
                purgeUnconfirmedStockRequirementsTitle: `Done.`,
            });
        } catch (err: unknown) {
            this.setState({
                purgeUnconfirmedStockRequirementsEnabled: true,
                purgeUnconfirmedStockRequirementsTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleDeleteOldReports(perform_delete: boolean) {
        this.setState({
            refreshDeleteOldReportsEnabled: false,
            refreshDeleteOldReportsTitle: "Waiting for server to finish...",
        });
        try {
            const res = await getBackend2().reports.deleteReports({ perform_delete });
            this.setState({
                refreshDeleteOldReportsEnabled: true,
                refreshDeleteOldReportsTitle: perform_delete
                    ? `Done, deleted ${res.deleted_count} reports.`
                    : `Finished with check, ${res.deleted_count} for deletion.`,
            });
        } catch (err: unknown) {
            this.setState({
                refreshDeleteOldReportsEnabled: true,
                refreshDeleteOldReportsTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleRunSimulation() {
        this.setState({
            runSimulationEnabled: false,
            runSimulationTitle: "Waiting for server to finish...",
        });
        try {
            await fullSimulation();
            this.setState({
                runSimulationEnabled: true,
                runSimulationTitle: `Simulation triggered.`,
            });
        } catch (err: unknown) {
            this.setState({
                runSimulationEnabled: true,
                runSimulationTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleRecomputeInsights() {
        this.setState({
            runRecalculateInsightsEnabled: false,
            runRecalculateInsightsTitle: "Waiting for server to finish...",
        });
        try {
            await fullInsights();
            this.setState({
                runRecalculateInsightsEnabled: true,
                runRecalculateInsightsTitle: `Recompute of insights finished.`,
            });
        } catch (err: unknown) {
            this.setState({
                runRecalculateInsightsEnabled: true,
                runRecalculateInsightsTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleRecomputeKPIs() {
        this.setState({
            runRecomputeKPIsEnabled: false,
            runRecomputeKPIsTitle: "Waiting for server to finish...",
        });
        try {
            await calculateKpis();
            this.setState({
                runRecomputeKPIsEnabled: true,
                runRecomputeKPIsTitle: `Recompute of KPIs triggered.`,
            });
        } catch (err: unknown) {
            this.setState({
                runRecomputeKPIsEnabled: true,
                runRecomputeKPIsTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleImportToolSetupMatrix() {
        this.setState({
            runToolSetupMatrixEnabled: false,
            runToolSetupMatrixTitle: "Waiting for server to finish...",
        });
        try {
            await getBackend2().models.updateModelToolSetupMatrix({
                matrix_format: "op_code_group",
                csv_parsing_format: this.state.toolSetupCsvParsingFormat,
            });
            this.setState({
                runToolSetupMatrixEnabled: true,
                runToolSetupMatrixTitle: "Tool setup matrix imported successfully.",
            });
        } catch (err: unknown) {
            this.setState({
                runToolSetupMatrixEnabled: true,
                runToolSetupMatrixTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleRunJobStatsKpi() {
        this.setState({
            runJobStatsKpiEnabled: false,
            runJobStatsKpiTitle: "Waiting for server to finish...",
        });
        try {
            const res = await getBackend2().reports.runJobStatsKpiReport({ overwrite: true });
            this.setState({
                runJobStatsKpiEnabled: true,
                runJobStatsKpiTitle: `Triggered, job id: ${res.uuid}.`,
            });
        } catch (err: unknown) {
            this.setState({
                runJobStatsKpiEnabled: true,
                runJobStatsKpiTitle: "Error: " + (err as Error).message,
            });
        }
    }

    // recompute knowledge graph
    async handleRecomputeKnowledgeGraph() {
        this.setState({
            runRecomputeKnowledgeGraphEnabled: false,
            runRecomputeKnowledgeGraphTitle: "Waiting for server to finish...",
        });
        try {
            await recomputeKnowledgeGraph();
            this.setState({
                runRecomputeKnowledgeGraphEnabled: true,
                runRecomputeKnowledgeGraphTitle: `Recompute of Knowledge Graph triggered.`,
            });
        } catch (err: unknown) {
            this.setState({
                runRecomputeKnowledgeGraphEnabled: true,
                runRecomputeKnowledgeGraphTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleAutocalculateLineCompetences() {
        this.setState({
            runAutocalculateLineCompetencesEnabled: false,
            runAutocalculateLineCompetencesTitle: "Waiting for server to finish...",
        });
        try {
            await getBackend2().manufacturing.autocalculateLineCompetences({});
            this.setState({
                runAutocalculateLineCompetencesEnabled: true,
                runAutocalculateLineCompetencesTitle: "Line competence autocalculate finished.",
            });
        } catch (err: unknown) {
            this.setState({
                runAutocalculateLineCompetencesEnabled: true,
                runAutocalculateLineCompetencesTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleUnconfirmedStock() {
        this.setState({
            runUnconfirmedStockEnabled: false,
            runUnconfirmedStockTitle: "Waiting for server to finish...",
        });
        try {
            await getBackend2().manufacturing.updateUnconfirmedStock({});
            this.setState({
                runUnconfirmedStockEnabled: true,
                runUnconfirmedStockTitle: `Unconfirmed stock triggered.`,
            });
        } catch (err: unknown) {
            this.setState({
                runUnconfirmedStockEnabled: true,
                runUnconfirmedStockTitle: "Error: " + (err as Error).message,
            });
        }
    }

    async handleUpdateProducedAvailable() {
        this.setState({
            runUpdateProducedAvailableEnabled: false,
            runUpdateProducedAvailableTitle: "Waiting for server to finish...",
        });
        try {
            await getBackend2().manufacturing.updateProducedAvailableIn({});
            this.setState({
                runUpdateProducedAvailableEnabled: true,
                runUpdateProducedAvailableTitle: `Update produced available triggered.`,
            });
        } catch (err: unknown) {
            this.setState({
                runUpdateProducedAvailableEnabled: true,
                runUpdateProducedAvailableTitle: "Error: " + (err as Error).message,
            });
        }
    }

    renderError = () => {
        if (this.state.message) {
            return <ErrorComponent msg={this.state.message} type="error" />;
        }
        return null;
    };

    /**
     * Rendering JSX for current component.
     */
    render() {
        return (
            <div className="container">
                <div className="row">
                    <div className="col-md-12">
                        <div className="white_box space10i">
                            <h2>Add missing dashboards</h2>
                            <div>
                                Inspects manufacturing database and re-generates all manufacturing dashboards. Line
                                dashboards will be generated if at least one order for that line exists.
                            </div>
                            <div>
                                {this.state.refreshDashboardsEnabled && (
                                    <button className="btn" onClick={() => this.handleRefreshDashboards()}>
                                        Refresh dashboards
                                    </button>
                                )}
                                <div>{this.state.refreshDashboardsTitle}</div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Delete dangling report files</h2>
                            <div>
                                Triggers clean-up operation on simulation-report file on server that have no associated
                                job record for them and are thus useless.
                            </div>
                            <div>
                                {this.state.refreshDeleteDanglingReportsEnabled && (
                                    <button className="btn" onClick={() => this.handleDeleteDanglingReports(false)}>
                                        Check files
                                    </button>
                                )}
                                {this.state.refreshDeleteDanglingReportsEnabled && (
                                    <button
                                        className="btn btn_margin_left"
                                        onClick={() => this.handleDeleteDanglingReports(true)}
                                    >
                                        Delete files
                                    </button>
                                )}
                                <div>{this.state.refreshDeleteDanglingReportsTitle}</div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Delete old report files</h2>
                            <div>
                                Triggers clean-up operation on simulation-reports that should be deleted according to
                                retention policy.
                            </div>
                            <div>
                                {this.state.refreshDeleteOldReportsEnabled && (
                                    <button className="btn" onClick={() => this.handleDeleteOldReports(false)}>
                                        Check old reports
                                    </button>
                                )}
                                {this.state.refreshDeleteOldReportsEnabled && (
                                    <button
                                        className="btn btn_margin_left"
                                        onClick={() => this.handleDeleteOldReports(true)}
                                    >
                                        Delete old reports
                                    </button>
                                )}
                                <div>{this.state.refreshDeleteOldReportsTitle}</div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Delete dangling stock-requirement records</h2>
                            <div>
                                Triggers clean-up operation for stock-requirement records that have not been confirmed
                                and are older than 24 hours.
                            </div>
                            <div>
                                {this.state.purgeUnconfirmedStockRequirementsEnabled && (
                                    <button
                                        className="btn btn_margin_left"
                                        onClick={() => this.handlePurgeStockRequirements()}
                                    >
                                        Delete files
                                    </button>
                                )}
                                <div>{this.state.purgeUnconfirmedStockRequirementsTitle}</div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Apply password policy</h2>
                            <div>
                                Triggers application of password policy - this will mark users with tag that forces them
                                to change password if it expired.
                            </div>
                            <div>
                                {this.state.runPwdPolicyEnabled && (
                                    <button className="btn" onClick={() => this.handleApplyPwdPolicy()}>
                                        Apply password policy
                                    </button>
                                )}
                                <div>{this.state.runPwdPolicyTitle}</div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Optimize job-file storage</h2>
                            <div>Triggers optimization of file-system location where job results are stored.</div>
                            <div>
                                {this.state.optimizeJobStorageEnabled && (
                                    <button className="btn" onClick={() => this.handleOptimizeJobStorage()}>
                                        Optimize
                                    </button>
                                )}
                                <div>{this.state.optimizeJobStorageTitle}</div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Log files</h2>
                            <div>Shows log files.</div>
                            <div>
                                {this.state.showLogFilesEnabled && (
                                    <button className="btn" onClick={() => this.handleLoadLogFiles()}>
                                        Show log files
                                    </button>
                                )}
                                <div>{this.state.showLogFilesTitle}</div>
                                {this.state.logFiles.map(x => (
                                    <div key={x.name}>
                                        <a
                                            href="#"
                                            onClick={() => {
                                                downloadAndOpenFile("/direct-file/" + x.token);
                                            }}
                                        >
                                            {" "}
                                            {x.name}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Run checks</h2>
                            <div>Runs internal checks that monitor manufacturing module execution and status.</div>
                            <div>
                                <div>
                                    {this.state.runChecksEnabled && (
                                        <button className="btn" onClick={() => this.handleRunChecks()}>
                                            Run checks
                                        </button>
                                    )}
                                </div>
                                <div>{this.state.runChecksCtrl}</div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Trigger processings</h2>
                            <div>Runs background processings.</div>
                            <div className="d-flex flex-wrap">
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runSimulationEnabled && (
                                            <button className="btn" onClick={() => this.handleRunSimulation()}>
                                                Run simulation
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runSimulationTitle}</div>
                                </div>
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runRecalculateInsightsEnabled && (
                                            <button className="btn" onClick={() => this.handleRecomputeInsights()}>
                                                Run insight recomputation
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runRecalculateInsightsTitle}</div>
                                </div>
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runRecomputeKPIsEnabled && (
                                            <button className="btn" onClick={() => this.handleRecomputeKPIs()}>
                                                Run KPIs recomputation
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runRecomputeKPIsTitle}</div>
                                </div>
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runJobStatsKpiEnabled && (
                                            <button className="btn" onClick={() => this.handleRunJobStatsKpi()}>
                                                Run job stats KPI
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runJobStatsKpiTitle}</div>
                                </div>
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runUnconfirmedStockEnabled && (
                                            <button className="btn" onClick={() => this.handleUnconfirmedStock()}>
                                                Run unconfirmed stock
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runUnconfirmedStockTitle}</div>
                                </div>
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runUpdateProducedAvailableEnabled && (
                                            <button
                                                className="btn"
                                                onClick={() => this.handleUpdateProducedAvailable()}
                                            >
                                                Run update produced available
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runUpdateProducedAvailableTitle}</div>
                                </div>
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runRecomputeKnowledgeGraphEnabled && (
                                            <button
                                                className="btn"
                                                onClick={() => this.handleRecomputeKnowledgeGraph()}
                                            >
                                                Run knowledge graph recomputation
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runRecomputeKnowledgeGraphTitle}</div>
                                </div>
                                <div className="mb-2 mr-2">
                                    <div>
                                        {this.state.runAutocalculateLineCompetencesEnabled && (
                                            <button
                                                className="btn"
                                                onClick={() => this.handleAutocalculateLineCompetences()}
                                            >
                                                Autocalculate line competences
                                            </button>
                                        )}
                                    </div>
                                    <div>{this.state.runAutocalculateLineCompetencesTitle}</div>
                                </div>
                            </div>
                        </div>
                        <div className="white_box space10i">
                            <h2>Import tool setup matrix</h2>
                            <div>
                                Imports tool setup matrix from CSV file <code>tool_setup_matrix.csv</code> into database
                                table.
                            </div>

                            <div className="mb-2 mr-2">
                                <div>
                                    {this.state.runToolSetupMatrixEnabled && (
                                        <>
                                            <select
                                                className="form-control d-inline-block w-auto mr-2"
                                                value={this.state.toolSetupCsvParsingFormat}
                                                onChange={e => {
                                                    this.setState({
                                                        toolSetupCsvParsingFormat: e.target.value as
                                                            | "all_to_all"
                                                            | "all_to_last",
                                                    });
                                                }}
                                            >
                                                <option value="all_to_all">CSV: all-to-all</option>
                                                <option value="all_to_last">CSV: all-to-last</option>
                                            </select>
                                            <button className="btn" onClick={() => this.handleImportToolSetupMatrix()}>
                                                Import tool setup matrix
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div>{this.state.runToolSetupMatrixTitle}</div>
                            </div>
                        </div>
                        {this.renderError()}
                    </div>
                </div>
            </div>
        );
    }
}

export default ManufacturingAdmin;
